import { supabase } from '../lib/supabaseClient.ts';

interface RSSItem {
  title: string;
  description: string;
  link: string;
  pubDate: string;
  category?: string;
}

interface GovernmentSource {
  name: string;
  url: string;
  type: 'rss' | 'scrape';
  selector?: string;
  alertKeywords: string[];
  cropKeywords: string[];
}

class RSSFeedService {
  private sources: GovernmentSource[] = [
    {
      name: 'FAO Lesotho',
      url: 'https://www.fao.org/lesotho/news/en/',
      type: 'scrape',
      alertKeywords: ['alert', 'warning', 'advisory', 'outbreak', 'drought', 'frost', 'pest', 'disease'],
      cropKeywords: ['maize', 'corn', 'beans', 'sorghum', 'wheat', 'livestock', 'cattle']
    },
    {
      name: 'FEWS NET Southern Africa',
      url: 'https://fews.net/southern-africa/lesotho',
      type: 'scrape',
      alertKeywords: ['food security', 'drought', 'crisis', 'emergency', 'alert', 'warning'],
      cropKeywords: ['maize', 'harvest', 'production', 'crops']
    },
    {
      name: 'ReliefWeb Lesotho',
      url: 'https://reliefweb.int/country/lso',
      type: 'scrape',
      alertKeywords: ['emergency', 'disaster', 'drought', 'flood', 'food security'],
      cropKeywords: ['agriculture', 'farming', 'crops', 'livestock']
    }
  ];

  // Parse RSS feed using CORS proxy
  private async parseRSSFeed(url: string): Promise<RSSItem[]> {
    try {
      // Skip RSS parsing due to CORS issues
      console.log('RSS parsing skipped due to CORS restrictions');
      return [];
      const data = await response.json();
      
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(data.contents, 'text/xml');
      
      const items = xmlDoc.querySelectorAll('item');
      const rssItems: RSSItem[] = [];
      
      items.forEach(item => {
        const title = item.querySelector('title')?.textContent || '';
        const description = item.querySelector('description')?.textContent || '';
        const link = item.querySelector('link')?.textContent || '';
        const pubDate = item.querySelector('pubDate')?.textContent || '';
        const category = item.querySelector('category')?.textContent || '';
        
        rssItems.push({ title, description, link, pubDate, category });
      });
      
      return rssItems;
    } catch (error) {
      console.error(`Failed to parse RSS feed ${url}:`, error);
      return [];
    }
  }

  // Scrape web content using CORS proxy
  private async scrapeWebContent(url: string): Promise<RSSItem[]> {
    try {
      // Skip web scraping due to CORS issues
      console.log('Web scraping skipped due to CORS restrictions');
      return [];
      const data = await response.json();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(data.contents, 'text/html');
      
      // Generic selectors for news/announcements
      const selectors = [
        'article',
        '.news-item',
        '.announcement',
        '.alert',
        '.post',
        'h2, h3',
        '.title'
      ];
      
      const items: RSSItem[] = [];
      
      for (const selector of selectors) {
        const elements = doc.querySelectorAll(selector);
        
        elements.forEach((element, index) => {
          if (index >= 10) return; // Limit to 10 items per selector
          
          const title = element.textContent?.trim().substring(0, 200) || '';
          const link = element.querySelector('a')?.href || url;
          
          if (title.length > 20) { // Only meaningful titles
            items.push({
              title,
              description: title,
              link,
              pubDate: new Date().toISOString()
            });
          }
        });
        
        if (items.length > 0) break; // Stop at first successful selector
      }
      
      return items;
    } catch (error) {
      console.error(`Failed to scrape ${url}:`, error);
      return [];
    }
  }

  // Check if content is agriculture-related
  private isAgriculturalContent(title: string, description: string, keywords: string[]): boolean {
    const content = (title + ' ' + description).toLowerCase();
    return keywords.some(keyword => content.includes(keyword.toLowerCase()));
  }

  // Determine alert severity based on keywords
  private determineSeverity(title: string, description: string): 'critical' | 'high' | 'medium' | 'low' {
    const content = (title + ' ' + description).toLowerCase();
    
    if (content.includes('emergency') || content.includes('critical') || content.includes('outbreak')) {
      return 'critical';
    }
    if (content.includes('warning') || content.includes('alert') || content.includes('urgent')) {
      return 'high';
    }
    if (content.includes('advisory') || content.includes('monitor')) {
      return 'medium';
    }
    return 'low';
  }

  // Extract affected crops from content
  private extractCrops(title: string, description: string, cropKeywords: string[]): string[] {
    const content = (title + ' ' + description).toLowerCase();
    return cropKeywords.filter(crop => content.includes(crop.toLowerCase()));
  }

  // Convert RSS/scraped item to alert
  private convertToAlert(item: RSSItem, source: GovernmentSource) {
    const severity = this.determineSeverity(item.title, item.description);
    const crops = this.extractCrops(item.title, item.description, source.cropKeywords);
    
    return {
      alert_type: 'government' as const,
      severity,
      title: item.title.substring(0, 200),
      description: item.description.substring(0, 500),
      location_coords: { lat: -29.3167, lng: 27.4833 }, // Maseru, Lesotho
      radius_km: 100, // Country-wide
      expiry_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
      crops_affected: crops.length > 0 ? crops : null,
      action_required: this.generateActionRequired(severity, item.title),
      source_type: source.name.toLowerCase().replace(/\s+/g, '_')
    };
  }

  // Generate action required based on severity and content
  private generateActionRequired(severity: string, title: string): string {
    const content = title.toLowerCase();
    
    if (content.includes('drought')) {
      return 'Implement water conservation, use drought-resistant varieties, monitor soil moisture';
    }
    if (content.includes('pest') || content.includes('armyworm')) {
      return 'Scout fields regularly, apply appropriate treatments, contact extension officers';
    }
    if (content.includes('frost')) {
      return 'Cover sensitive crops, harvest mature vegetables, monitor weather forecasts';
    }
    if (content.includes('disease')) {
      return 'Monitor crops for symptoms, improve sanitation, remove infected plants';
    }
    
    switch (severity) {
      case 'critical': return 'Take immediate action, contact local authorities';
      case 'high': return 'Monitor situation closely, prepare preventive measures';
      case 'medium': return 'Stay informed, follow recommended practices';
      default: return 'Stay updated with latest information';
    }
  }

  // Fetch and process all sources
  async fetchGovernmentAlerts(): Promise<void> {
    console.log('🔄 Fetching government alerts from RSS/web sources...');
    
    for (const source of this.sources) {
      try {
        console.log(`📡 Processing ${source.name}...`);
        
        let items: RSSItem[] = [];
        
        if (source.type === 'rss') {
          items = await this.parseRSSFeed(source.url);
        } else {
          items = await this.scrapeWebContent(source.url);
        }
        
        // Filter for agricultural content
        const agriculturalItems = items.filter(item => 
          this.isAgriculturalContent(item.title, item.description, source.alertKeywords) ||
          this.isAgriculturalContent(item.title, item.description, source.cropKeywords)
        );
        
        console.log(`📊 Found ${agriculturalItems.length} agricultural items from ${source.name}`);
        
        // Convert to alerts and save
        for (const item of agriculturalItems.slice(0, 5)) { // Limit to 5 per source
          const alert = this.convertToAlert(item, source);
          
          // Check if alert already exists
          const { data: existing } = await supabase
            .from('local_alerts')
            .select('id')
            .eq('title', alert.title)
            .eq('source_type', alert.source_type)
            .single();
          
          if (!existing) {
            const { error } = await supabase
              .from('local_alerts')
              .insert(alert);
            
            if (error) {
              console.error(`Failed to save alert from ${source.name}:`, error);
            } else {
              console.log(`✅ Saved alert: ${alert.title}`);
            }
          }
        }
        
        // Add delay between requests to be respectful
        await new Promise(resolve => setTimeout(resolve, 2000));
        
      } catch (error) {
        console.error(`Failed to process ${source.name}:`, error);
      }
    }
    
    console.log('✅ Government alerts fetch completed');
  }

  // Get last fetch time
  getLastFetchTime(): Date | null {
    const lastFetch = localStorage.getItem('last_government_alerts_fetch');
    return lastFetch ? new Date(lastFetch) : null;
  }

  // Set last fetch time
  setLastFetchTime(): void {
    localStorage.setItem('last_government_alerts_fetch', new Date().toISOString());
  }

  // Check if we should fetch (once per day)
  shouldFetch(): boolean {
    const lastFetch = this.getLastFetchTime();
    if (!lastFetch) return true;
    
    const hoursSinceLastFetch = (Date.now() - lastFetch.getTime()) / (1000 * 60 * 60);
    return hoursSinceLastFetch >= 24; // Fetch once per day
  }

  // Main method to fetch if needed
  async fetchIfNeeded(): Promise<void> {
    if (this.shouldFetch()) {
      await this.fetchGovernmentAlerts();
      this.setLastFetchTime();
    } else {
      console.log('⏭️ Skipping government alerts fetch (already fetched today)');
    }
  }
}

export const rssFeedService = new RSSFeedService();